import { TranslateFunc } from "utils/translation.utils";
import { IAddress } from "shared/domain/address/address";
import { AvailablePlatform } from "../../../providers/Auth/platform.provider";
import { hasGroupActiveCfmContracts, hasGroupPomUsers, IGroup } from "../group/group";
import { GroupType } from "../group/group-type";
import { IPaginationMeta } from "../pagination/pagination-meta";
import { Language } from "./language";
import { getRolePomTranslation, RolePom } from "./role-pom";
import { getUserTypeLevelTranslation, UserTypeLevel } from "./user-type-level";

export enum ResetPasswordStatus {
  Successful = "Successful",
  NoUser = "NoUser",
  WrongOldPass = "WrongOldPass",
  Failed = "Failed",
  CaptchaFailed = "CaptchaFailed",
}

export interface IUser {
  id: number;
  uuid: string;
  active: Date | null;
  deliveryEmail: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  jobTitle: string;
  language: Language;
  telephone: string;
  title: string;
  group?: IGroup;
  mustResetPassword: boolean;
  userTypeLevel: UserTypeLevel | null;
  externalId: string;
  addresses?: IAddress[];
  notify?: boolean;
  rolePom?: RolePom;
  createdAt?: Date;
  lastLogin: Date | null;
}

export interface IUserLoginAs {
  firstName: string | undefined;
  lastName: string | undefined;
  id: number;
}

export interface IUserResult {
  users: IUser[];
  meta?: IPaginationMeta;
}

export interface IResetPasswordResponse {
  message: string;
  status: ResetPasswordStatus;
}

export function userHasType(user: IUser | undefined, type: GroupType | undefined): boolean {
  if (!user || !type) {
    return false;
  }

  return user.group?.type === type;
}

// Shared
export function isAdmin(user: IUser | undefined): boolean {
  return userHasType(user, GroupType.Admin);
}

// CFM
export function isCfm(user: IUser | undefined): boolean {
  return isAdmin(user) || isCfmCustomer(user) || isCfmRecycler(user) || isCfmLogistics(user);
}

export function isCustomer(user: IUser | undefined): boolean {
  return userHasType(user, GroupType.Customer);
}

export function isCfmCustomer(user: IUser | undefined): boolean {
  return userHasType(user, GroupType.Customer) && (user?.group?.cfmContracts?.length ?? 0) > 0;
}

export function isCfmLogistics(user: IUser | undefined): boolean {
  return userHasType(user, GroupType.CfmLogistics);
}

export function isCfmRecycler(user: IUser | undefined): boolean {
  return userHasType(user, GroupType.CfmRecycler);
}

export function isAvalUser(user: IUser | undefined): boolean {
  return user?.group?.isAvalPartner ?? false;
}

// POM
export function isPom(user: IUser | undefined): boolean {
  return isAdmin(user) || isPomUser(user) || isPomRepresentative(user);
}

export function isPomAgency(user: IUser | undefined): boolean {
  return userHasType(user, GroupType.PomAgency);
}

export function isPomUser(user: IUser | undefined): boolean {
  return userHasType(user, GroupType.Customer) && (user?.group?.pomContracts?.length ?? 0) > 0;
}

export function isPomRepresentative(user: IUser | undefined): boolean {
  return userHasType(user, GroupType.PomRepresentative);
}

// roles
export const isCfmCorporate = (user: IUser | undefined): boolean => {
  return !!user && user.userTypeLevel === UserTypeLevel.Corporate;
};

export const isCfmWasteProducer = (user: IUser | undefined): boolean => {
  return !!user && user.userTypeLevel === UserTypeLevel.WasteProducer;
};

export function isPomMainContact(user: IUser | undefined): boolean {
  return (isPomUser(user) || isPomAgency(user) || isPomRepresentative(user)) && user?.rolePom === RolePom.MainContact;
}

export function isPomMainContactOrCfmCorporate(user: IUser | undefined): boolean {
  return isPomMainContact(user) || (isCfmCorporate(user) && isCfmCustomer(user));
}

// The following functions mirror the server-side rules for creating and editing users
// (UserAccessService and isActivePomMainContact in the core api)

// POM main contact with rights in the own group, customers need an active POM contract unlike in isPomMainContact
function isActivePomMainContact(user: IUser | undefined): boolean {
  return hasGroupPomUsers(user?.group) && user?.rolePom === RolePom.MainContact;
}

// CFM user type of the user, it only grants rights with an active CFM contract
function getActiveCfmUserTypeLevel(user: IUser | undefined): UserTypeLevel | null {
  return hasGroupActiveCfmContracts(user?.group) ? user?.userTypeLevel ?? null : null;
}

// for the platform the create user form is shown on: POM roles on Pom, CFM user types on Cfm
export function canManageUsers(user: IUser | undefined, platform: AvailablePlatform): boolean {
  if (isAdmin(user)) {
    return true;
  }

  switch (platform) {
    case AvailablePlatform.Pom:
      return isActivePomMainContact(user);
    case AvailablePlatform.Cfm:
      return getActiveCfmUserTypeLevel(user) !== null;
    default:
      return false;
  }
}

// Whether the editor may create and edit users of the group. Groups of other companies are only visible
// to the agencies they are assigned to, so the assignment is not checked here.
export function canManageUsersOfGroup(editor: IUser | undefined, group: IGroup): boolean {
  if (isAdmin(editor)) {
    return true;
  }

  if (group.type === GroupType.Admin) {
    return false;
  }

  if (group.id === editor?.group?.id) {
    return isActivePomMainContact(editor) || getActiveCfmUserTypeLevel(editor) !== null;
  }

  return isActivePomMainContact(editor) && isPomAgency(editor);
}

// Whether the editor may edit the target user of the group, the editor needs rights for every domain of the target.
// Waste producers may only edit waste producers within their own addresses. That is left to the server, because
// the user lists of waste producers only contain the matching addresses of a user.
export function canEditUser(editor: IUser | undefined, target: IUser, group: IGroup): boolean {
  if (isAdmin(editor)) {
    return true;
  }

  if (!canManageUsersOfGroup(editor, group)) {
    return false;
  }

  const isPomMainContactRights = isActivePomMainContact(editor);
  // CFM rights only apply within the own group
  const cfmUserTypeLevel = group.id === editor?.group?.id ? getActiveCfmUserTypeLevel(editor) : null;

  if (target.rolePom && !isPomMainContactRights) {
    return false;
  }

  // a CFM user type only applies in groups with an active CFM contract
  const isCfmUser = !!target.userTypeLevel && hasGroupActiveCfmContracts(group);
  const canManageCfmUser =
    cfmUserTypeLevel === UserTypeLevel.Corporate ||
    (cfmUserTypeLevel === UserTypeLevel.WasteProducer && target.userTypeLevel === UserTypeLevel.WasteProducer);
  if (isCfmUser && !canManageCfmUser) {
    return false;
  }

  const hasRole = !!target.rolePom || isCfmUser;
  return hasRole || isPomMainContactRights || cfmUserTypeLevel === UserTypeLevel.Corporate;
}

// Whether the initiator may log in as the target user of the group (LoginAsAccessService in the core api).
// Admins may log in as active users who are no admins, agencies only as active users with a POM role
// outside their own group. As for canManageUsersOfGroup, the agency assignment and the contracts are left to the server.
export function canLoginAs(
  initiator: IUser | undefined,
  target: IUser,
  targetGroup: IGroup | undefined,
  loggedAs: boolean,
): boolean {
  // during a login-as session the initiator is the target of that session, the server rejects nested sessions
  if (!initiator || loggedAs || !(isAdmin(initiator) || isPomAgency(initiator))) {
    return false;
  }

  if (target.id === initiator.id || !target.active || !targetGroup || targetGroup.type === GroupType.Admin) {
    return false;
  }

  if (isAdmin(initiator)) {
    return true;
  }

  return !!target.rolePom && targetGroup.id !== initiator.group?.id;
}

// misc
export const getGroupName = (user: IUser): string => {
  return user.group?.name ?? "";
};

export const getFullName = (user: IUser): string => {
  return `${user.firstName ?? ""} ${user.lastName ?? ""}`;
};

export const getCfmRoleName = (user: IUser, translate: TranslateFunc): string => {
  return user.userTypeLevel ? translate(getUserTypeLevelTranslation(user.userTypeLevel)) : "-";
};

export const getPomRoleName = (user: IUser, translate: TranslateFunc): string => {
  if (!user.rolePom) return "";
  return translate(getRolePomTranslation(user.rolePom));
};
